import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';
import { useGoogleMaps } from '../../../../utils/maps/GoogleMapsProvider';

interface MapPanelProps {
  retailers: RetailerHeaderModel[];
}

const RetailerListMap: React.FC<MapPanelProps> = ({ retailers }) => {
  const googleMapRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const navigate = useNavigate();
  const { loaded } = useGoogleMaps();

  const initialiseMap = (lat: number, lng: number, zoom: number): void => {
    if (googleMapRef.current) {
      const mapInstance = new window.google.maps.Map(googleMapRef.current, {
        center: { lat, lng },
        zoom,
      });
      mapRef.current = mapInstance;
    }
  };

  const getMarkerIcon = (statusId: number): google.maps.Symbol => {
    const colors = {
      inactive: {
        fill: '#F3F4F6',
        stroke: '#9CA3AF',
      },
      active: {
        fill: '#00B386',
        stroke: '#00583d',
      },
      problem: {
        fill: '#EF4444', 
        stroke: '#B91C1C',
      },
    };
  
    const colorSet =
      statusId === 1 ? colors.inactive :
        statusId === 2 ? colors.active :
          colors.problem;
  
    return {
      path: `M12 2C8.5 2 5.5 5 5.5 8.5c0 5.5 6.5 12.5 6.5 12.5s6.5-7 6.5-12.5C18.5 5 15.5 2 12 2zm0 7.5
        a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z`,
      fillColor: colorSet.fill,
      fillOpacity: 1,
      strokeColor: colorSet.stroke,
      strokeWeight: 1.5,
      scale: 1.75,
      anchor: new google.maps.Point(12, 28),
      labelOrigin: new google.maps.Point(12, 10),
    };
  };

  const addMarkers = (): void => {
    if (!mapRef.current) return;

    retailers.forEach((retailer) => {
      if (retailer.latitude && retailer.longitude) {
        const marker = new google.maps.Marker({
          position: { lat: retailer.latitude, lng: retailer.longitude },
          map: mapRef.current,
          title: retailer.name,
          icon: getMarkerIcon(retailer.statusId!),
        });

        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="
              background: white;
              padding: 16px;
              border-radius: 8px;
              box-shadow: 0 4px 16px rgba(0,0,0,0.08);
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              line-height: 1.5;
              max-width: 240px;
            ">
              <div style="margin-bottom: 8px;">
                <div style="
                  font-weight: 600;
                  font-size: 16px;
                  color: #111827;
                  margin-bottom: 2px;
                ">${retailer.name}</div>
                <div style="
                  color: #6B7280;
                  font-size: 14px;
                ">${retailer.country}</div>
              </div>
              <div style="margin-top: 12px;">
                <a 
                  href="#" 
                  id="view-retailer-${retailer.retailerId}" 
                  style="
                    display: inline-flex;
                    align-items: center;
                    background-color: #f9fafb;
                    color: #00583d;
                    font-weight: 500;
                    padding: 6px 12px;
                    border-radius: 6px;
                    text-decoration: none;
                    font-size: 14px;
                    transition: all 0.2s ease;
                    border: 1px solid #e5e7eb;
                  "
                  onmouseover="this.style.backgroundColor='#f3f4f6'; this.style.boxShadow='0 1px 2px rgba(0,0,0,0.05)'"
                  onmouseout="this.style.backgroundColor='#f9fafb'; this.style.boxShadow='none'"
                >
                  View details
                </a>
              </div>
            </div>
          `,
        });
        marker.addListener('click', () => {
          infoWindow.open(mapRef.current, marker);
          mapRef.current?.setZoom(15);
          mapRef.current?.setCenter(marker.getPosition() as google.maps.LatLng);

          setTimeout(() => {
            const link = document.getElementById(`view-retailer-${retailer.retailerId}`);
            if (link) {
              link.addEventListener('click', (e) => {
                e.preventDefault();
                navigate(`/retailers/${retailer.retailerId}`);
              });
            }
          }, 0);
        });
      }
    });
  };

  const fitMapToMarkers = () => {
    if (!mapRef.current || !retailers.length) return;
    const validRetailers = retailers.filter(r => r.latitude && r.longitude);
    if (validRetailers.length === 1) {
      const r = validRetailers[0];
      mapRef.current.setCenter({ lat: r.latitude!, lng: r.longitude! });
      mapRef.current.setZoom(10);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    let hasValid = false;
    validRetailers.forEach(r => {
      bounds.extend({ lat: r.latitude!, lng: r.longitude! });
      hasValid = true;
    });
    if (hasValid) {
      mapRef.current.fitBounds(bounds);
      const listener = window.google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
        if (mapRef.current && mapRef.current.getZoom()! > 15) {
          mapRef.current.setZoom(15);
        }
      });
    }
  };

  useEffect(() => {
    if (!loaded) return;
    if (retailers.length && retailers.some(r => r.latitude && r.longitude)) {
      initialiseMap(50.5, 10.0, 4);
      addMarkers();
      setTimeout(fitMapToMarkers, 0);
    } else {
      initialiseMap(50.5, 10.0, 4);
      addMarkers();
    }
  }, [loaded, retailers]);

  return (
    <div
      id="map"
      ref={googleMapRef}
      style={{ width: '100%', height: '100%', border: '1px solid #ccc', borderRadius: '12px' }}
    ></div>
  );
};

export default RetailerListMap;